import { Link } from "react-router-dom";
import { BookOpen, MessageSquare, Star } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";

const guideDownloads = [
  {
    title: "Buyer’s Guide",
    description: "A practical guide to navigating the home buying process with more clarity and confidence.",
    href: "/guides/Hadassah-Homes-Buyers-Guide-Website.pdf",
    cta: "Download Buyer’s Guide",
  },
  {
    title: "Seller’s Guide",
    description: "A preparation-first guide to selling with stronger presentation, pricing, and strategy.",
    href: "/guides/Hadassah-Homes-Sellers-Guide-Website.pdf",
    cta: "Download Seller’s Guide",
  },
] as const;

const resources = [
  {
    icon: BookOpen,
    title: "Blog",
    description: "Insights, tips, and market updates from the Hadassah Homes team.",
    href: "/blog",
  },
  {
    icon: Star,
    title: "Success Stories",
    description: "Real stories from clients who trusted Hadassah Homes with their journey.",
    href: "/success-stories",
  },
  {
    icon: MessageSquare,
    title: "FAQ",
    description: "Common questions about our services, process, and markets.",
    href: "/faq",
  },
];

const ResourcesLanding = () => (
  <SiteLayout>
    <PageHero
      title="Resources"
      subtitle="Guides, stories, and insights to help you navigate real estate with confidence."
    />
    <ContentSection>
      <div className="mb-12 space-y-6 md:mb-16">
        <div className="max-w-2xl space-y-3">
          <p className="font-body text-xs uppercase tracking-[0.14em] text-accent">
            Featured Guides
          </p>
          <h2 className="font-headline text-2xl md:text-3xl text-foreground">
            Download the Hadassah Homes Buyer and Seller Guides
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Start with the guide that matches your next move, then come back to us when you want help applying it to your situation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {guideDownloads.map((guide) => (
            <div
              key={guide.href}
              className="bg-card border border-border p-6 md:p-8"
            >
              <BookOpen className="w-6 h-6 text-accent mb-4" strokeWidth={1.5} />
              <h3 className="font-headline text-lg md:text-xl text-foreground mb-3">
                {guide.title}
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                {guide.description}
              </p>
              <a
                href={guide.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-6 font-body text-xs uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
              >
                {guide.cta}
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {resources.map((r) => (
          <Link
            key={r.href}
            to={r.href}
            className="group bg-card border border-border p-6 md:p-8 transition-colors hover:border-accent/40"
          >
            <r.icon className="w-6 h-6 text-accent mb-4" strokeWidth={1.5} />
            <h3 className="font-headline text-lg md:text-xl text-foreground mb-2 group-hover:text-accent transition-colors">
              {r.title}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{r.description}</p>
            <span className="inline-block mt-4 font-body text-xs uppercase tracking-widest text-accent">
              Explore →
            </span>
          </Link>
        ))}
      </div>
    </ContentSection>
  </SiteLayout>
);

export default ResourcesLanding;
